import type { ContextUsage } from "@/types/command-agent";

interface ContextUsageCarrier {
    role?: string;
    contextUsage?: ContextUsage | null;
    branches?: Array<{ contextUsage?: ContextUsage | null }>;
    activeBranchIndex?: number;
}

function readFiniteNumber(value: unknown): number | null {
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function usageFromMessage(message: ContextUsageCarrier | undefined): ContextUsage | null {
    if (!message) return null;
    const branch =
        message.branches && message.branches.length > 0
            ? message.branches[message.activeBranchIndex ?? message.branches.length - 1]
            : undefined;
    return branch?.contextUsage ?? message.contextUsage ?? null;
}

/** Accepts a stream `context_usage` event or `message_metadata.context_usage`. */
export function parseContextUsage(raw: unknown): ContextUsage | null {
    if (!raw || typeof raw !== "object") return null;
    const record = raw as Record<string, unknown>;
    const nested = record.context_usage;
    if (
        nested &&
        typeof nested === "object" &&
        record.used_tokens == null &&
        record.usedTokens == null
    ) {
        return parseContextUsage(nested);
    }
    const usedTokens = readFiniteNumber(record.used_tokens ?? record.usedTokens);
    const contextWindow = readFiniteNumber(record.context_window ?? record.contextWindow);
    if (usedTokens === null || contextWindow === null || contextWindow <= 0) return null;

    const remainingTokens = readFiniteNumber(
        record.remaining_tokens ?? record.remainingTokens
    );
    const model = typeof record.model === "string" ? record.model : undefined;

    const compacted =
        record.compacted === true || record.auto_compacted === true || record.autoCompacted === true;

    return {
        usedTokens,
        contextWindow,
        ...(remainingTokens !== null ? { remainingTokens } : {}),
        ...(model ? { model } : {}),
        ...(compacted ? { compacted: true } : {}),
    };
}

export function contextUsagePercent(usage: ContextUsage): number {
    return Math.min(100, Math.max(0, (usage.usedTokens / usage.contextWindow) * 100));
}

export function formatTokenCount(value: number): string {
    return Math.round(value).toLocaleString("en-GB");
}

/** Newest message that already has context usage, walking from the end of the thread. */
export function pickLatestContextUsage(
    messages: ContextUsageCarrier[]
): ContextUsage | null {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const usage = usageFromMessage(messages[index]);
        if (usage) return usage;
    }
    return null;
}
